import { parseInteger } from '@e2m-customer-portal/shared';
import { add, isBefore, isPast, startOfToday, startOfTomorrow } from 'date-fns';
import { localizeDate } from './localize-date';
import { Deadline, MarketingDeadline } from '../generated/graphql';

export type { Deadline, MarketingDeadline } from '../generated/graphql';

/**
 * For a given deadline specification, calculates the deadline date based on the current date.
 *
 * @param deadline
 */
export function getMarketingDeadline(deadline: MarketingDeadline): Date {
  const { deadlineForEarliestForecast, daysBeforeDelivery } = deadline;
  const [hours, minutes, seconds] = deadlineForEarliestForecast.split(':').map(parseInteger);

  const cutOffDate = new Date(new Date().setHours(hours, minutes, seconds));

  const baseDate = isPast(localizeDate(cutOffDate)) ? startOfTomorrow() : startOfToday();
  return add(baseDate, { days: daysBeforeDelivery });
}

/**
 * Finds the most restrictive deadline from an array of deadlines.
 *
 * @param deadlines
 */
export function getEarliestMarketingDeadline(deadlines: readonly (Deadline | MarketingDeadline)[]): MarketingDeadline {
  if (!deadlines.length) {
    throw new Error('At least one deadline must be given.');
  }

  const currentDeadlines = deadlines
    .map((dl) => ('type' in dl ? dl.deadline : dl))
    .reduce((previous, current) => {
      // reducer to find the most restrictive deadline:
      // first, finds the largest `daysBeforeDelivery`, then the earliest `deadlineForEarliestForecast`
      if (current && (!previous || current.daysBeforeDelivery > previous.daysBeforeDelivery)) {
        return current;
      }
      if (previous && (!current || current.daysBeforeDelivery < previous.daysBeforeDelivery)) {
        return previous;
      }
      if (current && previous && current.deadlineForEarliestForecast < previous.deadlineForEarliestForecast) {
        return current;
      }
      return previous;
    });
  if (!currentDeadlines) {
    throw new Error('At least one deadline must be given.');
  }
  return currentDeadlines;
}

/**
 * Determines if the given deadline has already passed for the given date.
 *
 * @param deadline
 * @param date
 */
export function isAfterDeadline(deadline: MarketingDeadline, date: number | Date): boolean {
  const deadlineDate = getMarketingDeadline(deadline);
  return isBefore(date, deadlineDate);
}
